import {
    useCentralErrorSetter,
    useGetErrorInfo,
} from '@experiences/error';
import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import {
    logEvent,
    portalTelemetry,
} from '@experiences/telemetry';
import {
    GlobalStyles,
    useApolloTheme,
} from '@experiences/theme';
import { useUiDataContext } from '@experiences/ui-common';
import {
    getFullNameDuplicated,
    useRouteResolver,
    useShowDialog,
} from '@experiences/util';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import AddIcon from '@mui/icons-material/Add';
import AddToPhotosIcon from '@mui/icons-material/AddToPhotos';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import EditIcon from '@mui/icons-material/Edit';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import PersonOutlined from '@mui/icons-material/PersonOutline';
import {
    Chip,
    ListItemIcon,
    Menu,
    MenuItem,
    Tooltip,
    Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import Tokens from '@uipath/apollo-core';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    FormattedDate,
    useIntl,
} from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import type { Row } from 'react-table';
import useSWR from 'swr';

import { notificationType } from '../../common/constants/Constant';
import * as RouteNames from '../../common/constants/RouteNames';
import { UserType } from '../../common/constants/UsersConstant';
import useSimpleGroup from '../../common/hooks/SimpleGroup';
import { useCheckAADEnabled } from '../../common/hooks/useCheckAADEnabled';
import { useCheckSSOEnabled } from '../../common/hooks/useCheckSSOEnabled';
import { useIsAdminRevampEnabled } from '../../common/hooks/useIsAdminRevampEnabled';
import { useLicenseExpired } from '../../common/hooks/useLicenseExpired';
import { useLoginProvider } from '../../common/hooks/useLoginProvider';
import type { IUserCIS } from '../../common/interfaces/cis/user';
import AllocateLicenseIcon from '../../images/icons/AllocateLicenseIcon';
import ExternalLicenseIcon from '../../images/icons/ExternalLicenseIcon';
import { checkIfOnlyUserInGroup } from '../../services/identity/GroupService';
import { createInviteUrl } from '../../services/identity/UserInvite';
import getUsersInPartition, {
    getUsersInPartitionWithLicenses,
    userPartitionUrl,
} from '../../services/identity/UserPartitionService';
import { deleteUsers } from '../../services/identity/UserService';
import {
    accountGlobalId,
    accountType,
    companyName,
    EnableUserLicensingSelector,
    isAdminSelector,
    isHostModeSelector,
    isUnlicensedSelector,
    organizationLanguage,
    userGlobalId,
} from '../../store/selectors';
import type {
    IAction,
    IActionHeader,
} from '../common/UiGrid/grid';
import {
    ButtonType,
    GridActionType,
} from '../common/UiGrid/grid';
import { UiPaginatedGrid } from '../common/UiPaginatedGrid';
import { UserGroup } from '../common/UserGroups';
import { groupUrl } from './GroupsPageComponent';
import type { IUserLicenses } from './interfaces/userLicense';
import ResendInvitationDialogBody from './subcomponents/ResendInvitationDialogBody';

type IUserWithLicenses = IUserCIS & Partial<IUserLicenses>;

export interface IUserCISResponse {
    totalCount: number;
    results: IUserWithLicenses[];
}

const useStyles = makeStyles(theme => ({
    ...GlobalStyles(theme),
    ...createStyles({
        userNameCell: { display: 'flex' },
        userRoleLabel: {
            fontSize: '11px',
            fontWeight: 'bold',
            marginLeft: '8px',
            backgroundColor: theme.palette.semantic.colorBackgroundHigh,
            height: '18px',
            marginTop: '1px',
            '&:hover': { backgroundColor: theme.palette.semantic.colorBackgroundHigh },
        },
        azureDescription: {
            marginTop: '8px',
            marginBottom: '8px',
        },
        resendButton: {
            marginLeft: '8px',
            fontWeight: 600,
            color: theme.palette.semantic.colorPrimary,
            backgroundColor: theme.palette.semantic.colorBackground,
            borderWidth: '0px',
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' },
        },
        licenseIcon: {
            height: '20px',
            width: '20px',
            marginRight: '4px',
        },
        tableActionButton: {
            color: theme.palette.semantic.colorPrimary,
            fontSize: '14px',
        },
        leftSpacing: { marginLeft: '5px' },
    }),
}));

const UsersCISPageComponent: React.FC = () => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const { enqueueSnackbar } = useSnackbar();
    const { themeId } = useApolloTheme();
    const { getErrorMessage } = useGetErrorInfo();

    const history = useHistory();
    const getRoute = useRouteResolver();

    const { data: { refresh: refreshData } } = useUiDataContext<{ refresh: boolean }>();

    const userId = useSelector(userGlobalId);
    const isAdmin = useSelector(isAdminSelector);
    const EnableUserLicensing = useSelector(EnableUserLicensingSelector);
    const partitionGlobalId = useSelector(accountGlobalId);
    const orgLanguage = useSelector(organizationLanguage);
    const orgName = useSelector(companyName);
    const isUnlicensedMode = useSelector(isUnlicensedSelector);
    const isHostMode = useSelector(isHostModeSelector);
    const subscriptionPlan = useSelector(accountType);

    const {
        loading: groupLoading, getGroupsForUser,
    } = useSimpleGroup(true);

    const isSSOConnectionEnabled = useCheckSSOEnabled();
    const isLicenseExpired = useLicenseExpired();
    const isAADConnectionEnabled = useCheckAADEnabled();
    const createDialog = useShowDialog();
    const loginProvider = useLoginProvider();
    const setErrorMessage = useCentralErrorSetter();

    const {
        data: lastAdminInOrg, isValidating,
    } = useSWR<Boolean, Error>(
        !process.buildConfigs.disableUsersLeaveOrg && isAdmin && !isSSOConnectionEnabled
            ? [ `${groupUrl}/:id/checkIfOnlyUserInGroup`, partitionGlobalId, userId, UserGroup.Administrators ]
            : null,
        checkIfOnlyUserInGroup,
    );

    const [ refresh, setRefresh ] = useState(false);
    const [ loading, setLoading ] = useState(false);
    const [ disabledEmails, setDisabledEmails ] = React.useState<string[]>([]);

    const dataUrl = useMemo(
        () => (EnableUserLicensing && isAdmin ? `${userPartitionUrl}/licenses` : `${userPartitionUrl}/users`),
        [ EnableUserLicensing, isAdmin ],
    );

    useEffect(() => {
        setLoading(groupLoading || isValidating);
    }, [ groupLoading, isValidating ]);

    useEffect(() => {
        setRefresh(refreshData);
    }, [ refreshData ]);

    const deleteUsersAsync = useCallback(
        async (users: IUserCIS[], deleteCurrentUser: boolean = false) => {
            try {
                if (users.length > 0) {
                    const userIds = users.map(user => user.id);
                    enqueueSnackbar(translate({ id: 'CLIENT_USER_DELETED' }), { variant: notificationType.INPROGRESS as any });
                    await deleteUsers(partitionGlobalId, userIds, deleteCurrentUser, isHostMode);

                    enqueueSnackbar(translate({ id: 'CLIENT_SUCESSFULLY_DELETED_USERS' }), { variant: notificationType.SUCCESS as any });
                    logEvent('User(s) deleted', {
                        name: 'Delete',
                        group: 'Users',
                        code: 'INV-USERS-05',
                    }, {
                        'SubscriptionType': subscriptionPlan,
                        'NoOfDeletedUsers': users.length,
                    });
                    setRefresh(true);
                }
            } catch (error) {
                setErrorMessage(await getErrorMessage(error));
            }
        },
        [ enqueueSnackbar, translate, partitionGlobalId, isHostMode, subscriptionPlan, setErrorMessage, getErrorMessage ],
    );

    const openDeleteDialog = useCallback(
        async (rows: Array<Row<IUserCIS>> | Row<IUserCIS>) => {
            const users: IUserCIS[] = Array.isArray(rows) ? rows.map(row => row.original) : [ rows.original ];

            const proceed = await createDialog({
                title: translate({ id: 'CLIENT_DELETE_USER' }),
                body: `${translate(
                    { id: 'CLIENT_SHOULD_DELETE_USERS' },
                    {
                        0:
              users.length === 1
                  ? `${users[0].name}${users[0].surname ? ' ' + users[0].surname : ''}`
                  : translate({ id: 'CLIENT_SELECTED_USERS_TEXT' }),
                    },
                )} ${translate({ id: 'CLIENT_MULTIPLE_CHECKED_USER_DELETE' })}`,
                icon: 'warning',
                showCancel: true,
                primaryButtonText: translate({ id: 'CLIENT_DELETE' }),
            });
            if (proceed) {
                const filteredUsers = users.filter(user => user.id !== userId);
                deleteUsersAsync(filteredUsers);
            }
        },
        [ translate, createDialog, deleteUsersAsync, userId ],
    );

    const openLeaveOrgDialog = useCallback(
        async (row: Row<IUserCIS>) => {
            const proceed = await createDialog({
                title: translate({ id: 'CLIENT_LEAVE_ORG' }),
                body: `${translate({ id: 'CLIENT_SHOULD_LEAVE_ORG' }, { name: orgName })}${translate({ id: 'CLIENT_SINGLE_USER_DELETE_SUFFIX' })}`,
                icon: 'warning',
                showCancel: true,
                primaryButtonText: translate({ id: 'CLIENT_CONFIRM' }),
            });
            if (proceed) {
                const currentUser = [ row.original ];
                try {
                    await deleteUsersAsync(currentUser, true);
                    enqueueSnackbar(translate({ id: 'CLIENT_USER_LEAVE_ORG' }), { variant: notificationType.INPROGRESS as any });
                    history.push('/portal_/logout');
                } catch (error) {
                    enqueueSnackbar(translate({ id: 'CLIENT_USER_LEAVE_FAILED' }), { variant: notificationType.ERROR as any });
                }
            }
        },
        [ createDialog, translate, orgName, deleteUsersAsync, enqueueSnackbar, history ],
    );

    const openAdminLeaveOrgDialog = useCallback(
        async (row: Row<IUserCIS>) => {
            if (!lastAdminInOrg) {
                openLeaveOrgDialog(row);
            } else {
                await createDialog({
                    title: translate({ id: 'CLIENT_UNABLE_LEAVE_ORG' }),
                    body: translate({ id: 'CLIENT_LEAVE_ORG_MIN_ONE_ADMIN' }),
                    icon: 'error',
                    primaryButtonText: 'OK',
                });
            }
        },
        [ translate, createDialog, openLeaveOrgDialog, lastAdminInOrg ],
    );

    const openResendInviteDialog = useCallback(
        async (row: Row<IUserCIS>) => {
            const inviteUrl = createInviteUrl(
                window.location.origin,
                partitionGlobalId,
                row.original.email,
                loginProvider,
                orgName,
            );

            await createDialog({
                title: translate({ id: 'CLIENT_RESEND_INVITE' }),
                customDialogContent: ResendInvitationDialogBody,
                customDialogContentProps: {
                    row,
                    orgLanguage,
                    orgName,
                    disabledEmails,
                    setDisabledEmails,
                    loginProvider,
                    inviteUrl,
                },
            });
        },
        [ createDialog, translate, partitionGlobalId, disabledEmails, orgLanguage, orgName, loginProvider ],
    );

    const [ anchorEl, setAnchorEl ] = React.useState(null);
    const open = useMemo(() => Boolean(anchorEl), [ anchorEl ]);
    const DisableFeatureFedRamp = useFeatureFlagValue(Features.DisableFeatureFedRamp.name);
    const EnableAddDirectoryUsers = useFeatureFlagValue(Features.EnableAddDirectoryUsers.name) && !DisableFeatureFedRamp;
    const ShowInviteUserButton = useFeatureFlagValue(Features.ShowInviteUserButton.name);

    const isAdminRevampEnabled = useIsAdminRevampEnabled();

    const EnableUserInvite = useMemo(
        () =>
            !isUnlicensedMode && !process.buildConfigs.disableUserInvite && ShowInviteUserButton && !DisableFeatureFedRamp,
        [ DisableFeatureFedRamp, ShowInviteUserButton, isUnlicensedMode ],
    );

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const handleClick = useCallback(event => {
        setAnchorEl(event.currentTarget);
    }, []);

    const handleInviteUser = useCallback(() => {
        history.push(`${getRoute(RouteNames.Users)}/invite`);
        handleClose();
    }, [ getRoute, handleClose, history ]);

    const handleBulkInviteUsers = useCallback(() => {
        portalTelemetry.trackTrace({
            message: `Clicked on Upload CSV button`,
            severityLevel: SeverityLevel.Information,
        }, { revampEnabled: isAdminRevampEnabled });
        history.push(`${getRoute(RouteNames.Users)}/bulkinvite`);
    }, [ getRoute, history, isAdminRevampEnabled ]);

    const handleAddDirectoryUser = useCallback(() => {
        history.push(`${getRoute(RouteNames.DirectoryUser)}/add`);
        handleClose();
    }, [ getRoute, handleClose, history ]);

    const extraActionHeaderButtons: IActionHeader[] = useMemo(() => {
        const actionList: IActionHeader[] = [];

        const subMenuList = [
            <MenuItem
                key={0}
                data-cy="invite-users-button"
                onClick={handleInviteUser}
                disableRipple>
                <ListItemIcon className={clsx(classes.icon, classes.svgIcon)}>
                    <AddIcon />
                </ListItemIcon>
                {translate({ id: 'CLIENT_INVITE_USER' })}
            </MenuItem>,
            <MenuItem
                key={1}
                data-cy="add-directory-user-button"
                onClick={handleAddDirectoryUser}
                disableRipple>
                <ListItemIcon className={clsx(classes.icon, classes.svgIcon)}>
                    <AddIcon />
                </ListItemIcon>
                {translate({ id: 'CLIENT_ADD_DIRECTORY_USER' })}
            </MenuItem>,
        ];

        const addUsers: IActionHeader = {
            type: ButtonType.ButtonWithIcon,
            label: translate({ id: 'CLIENT_ADD_USER' }),
            icon: <AddIcon />,
            endIcon: <KeyboardArrowDownIcon />,
            click: handleClick,
            invisible: !(isAdmin && EnableUserInvite && EnableAddDirectoryUsers),
            variant: 'contained',
            dataCy: 'add-user-button',
            id: 'add-user-button',
            ariaControls: 'add-user-menu',
            ariaHaspopup: 'true',
            ariaExpanded: open ? 'true' : undefined,
            subMenuList,
            className: clsx(classes.leftSpacing),
            children: (
                <Menu
                    id="add-user-menu"
                    MenuListProps={{ 'aria-labelledby': 'add-user-button' }}
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    disableAutoFocusItem
                >
                    {subMenuList.map(subMenu => subMenu)}
                </Menu>
            ),
        };

        const addUsersOnPrem: IActionHeader = {
            type: ButtonType.ButtonWithIcon,
            label: translate({ id: 'CLIENT_ADD_USER' }),
            icon: <AddIcon />,
            click: () => history.push(`${getRoute(RouteNames.Users)}/add`),
            invisible: !isAdmin,
            variant: 'contained',
            className: clsx(classes.leftSpacing),
            dataCy: 'add-user-button',
        };

        const inviteUsersBulk: IActionHeader = {
            type: ButtonType.ButtonWithIcon,
            label: translate({ id: 'CLIENT_BULK_INVITE' }),
            icon: <AddToPhotosIcon />,
            click: handleBulkInviteUsers,
            invisible: !(isAdmin && EnableUserInvite),
            variant: 'text',
            className: clsx(classes.tableActionButton, classes.leftSpacing),
            dataCy: 'bulk-invite-users-button',
        };

        const inviteUsers: IActionHeader = {
            type: ButtonType.ButtonWithIcon,
            label: translate({ id: 'CLIENT_INVITE_USER' }),
            icon: <AddIcon />,
            click: handleInviteUser,
            invisible: !(isAdmin && EnableUserInvite && !EnableAddDirectoryUsers),
            variant: 'contained',
            className: clsx(classes.leftSpacing),
            dataCy: 'invite-users-button',
        };

        const addDirectoryUsers: IActionHeader = {
            type: ButtonType.ButtonWithIcon,
            label: translate({ id: 'CLIENT_ADD_DIRECTORY_USER' }),
            icon: <AddIcon />,
            click: handleAddDirectoryUser,
            invisible: !(isAdmin && !EnableUserInvite && EnableAddDirectoryUsers),
            variant: 'contained',
            className: clsx(classes.leftSpacing),
            dataCy: 'add-directory-user-button',
        };
        process.buildConfigs.name === 'msi' || process.buildConfigs.name === 'k8s'
            ? actionList.push(inviteUsersBulk, addUsersOnPrem, inviteUsers, addDirectoryUsers)
            : actionList.push(inviteUsersBulk, addUsers, inviteUsers, addDirectoryUsers);

        return actionList;
    }, [
        handleInviteUser,
        classes.icon,
        classes.svgIcon,
        classes.tableActionButton,
        classes.leftSpacing,
        translate,
        handleAddDirectoryUser,
        handleClick,
        isAdmin,
        EnableUserInvite,
        EnableAddDirectoryUsers,
        open,
        anchorEl,
        handleClose,
        handleBulkInviteUsers,
        history,
        getRoute,
    ]);

    const getRowActions = useCallback(() => {
        const actions: Array<IAction<IUserCIS>> = [];
        if (isAdmin) {
            if (EnableUserLicensing && !isUnlicensedMode) {
                actions.push({
                    type: ButtonType.Icon,
                    label: translate({ id: 'CLIENT_EDIT_USER_EXPLICIT_ALLOCATION' }),
                    tooltip: translate({ id: 'CLIENT_EDIT_USER_EXPLICIT_ALLOCATION' }),
                    actionType: GridActionType.Row,
                    icon: <AllocateLicenseIcon />,
                    dataCy: 'ui-grid-edit-user-allocations-button',
                    click: row => {
                        history.push({
                            pathname: `${getRoute(RouteNames.UsersUserEditLicenseAllocation)}`,
                            state: {
                                user: row.original,
                                previousLocation: location.pathname,
                            },
                        });
                    },
                    disable: () => isLicenseExpired,
                });
            }

            actions.push({
                type: ButtonType.Icon,
                label: translate({ id: 'CLIENT_EDIT' }),
                tooltip: translate({ id: 'CLIENT_EDIT' }),
                actionType: GridActionType.Row,
                icon: <EditIcon />,
                click: row => {
                    const user: IUserCIS = row.original;
                    history.push({
                        pathname: `${getRoute(RouteNames.Users)}/edit`,
                        state: { user },
                    });
                },
                dataCy: 'edit-user-button',
            });
            actions.push({
                type: ButtonType.Icon,
                label: translate({ id: 'CLIENT_DELETE' }),
                tooltip: translate({ id: 'CLIENT_DELETE' }),
                actionType: GridActionType.Row,
                icon: <DeleteForeverOutlinedIcon />,
                invisible: row => row?.original?.id === userId,
                click: openDeleteDialog,
                dataCy: 'delete-user-button',
            });
        }

        if (!process.buildConfigs.disableUsersLeaveOrg) {
            actions.push({
                type: ButtonType.Icon,
                label: translate({ id: 'CLIENT_LEAVE_ORG' }),
                tooltip: translate({ id: 'CLIENT_LEAVE_ORG' }),
                actionType: GridActionType.Row,
                icon: <ExitToAppIcon />,
                invisible: row => row?.original?.id !== userId,
                click: isAdmin ? openAdminLeaveOrgDialog : openLeaveOrgDialog,
                dataCy: 'leave-org-button',
            });
        }

        return actions;
    }, [
        getRoute,
        history,
        isAdmin,
        isUnlicensedMode,
        openDeleteDialog,
        openLeaveOrgDialog,
        openAdminLeaveOrgDialog,
        userId,
        translate,
        EnableUserLicensing,
        isLicenseExpired,
    ]);

    const getLicenseEntry = useCallback(
        (user: IUserWithLicenses) => {
            const text = user.useExternalLicense
                ? translate({ id: 'CLIENT_GROUPS_EXTERNAL_LICENSE' })
                : user.userBundleCodes?.map(ubl => translate({ id: `CLIENT_${ubl}` })).join(', ');

            const tooltip = user.inheritedFromGroup ? 'CLIENT_INHERIT_FROM_GROUP' : 'CLIENT_ALLOCATED_TO_USER';

            const Icon = user.useExternalLicense
                ? ExternalLicenseIcon
                : user.inheritedFromGroup
                    ? PeopleOutlineOutlinedIcon
                    : PersonOutlined;

            return {
                text,
                tooltip: text ? translate({ id: tooltip }) : '',
                Icon: text ? Icon : null,
            };
        },
        [ translate ],
    );

    return (
        <>
            {isAADConnectionEnabled && (
                <Typography className={classes.azureDescription}>
                    {translate({
                        id:
              DisableFeatureFedRamp || process.buildConfigs.isOnPrem
                  ? 'CLIENT_AZURE_USERS_DESCRIPTION_NO_INVITE'
                  : 'CLIENT_AZURE_USERS_DESCRIPTION',
                    })}
                </Typography>
            )}
            <UiPaginatedGrid<IUserWithLicenses, IUserCISResponse>
                url={dataUrl}
                fetcher={EnableUserLicensing && isAdmin ? getUsersInPartitionWithLicenses : getUsersInPartition}
                fetcherArgs={[ partitionGlobalId ]}
                data-cy="users-ui-grid"
                loading={loading}
                search
                searchPlaceholder={translate({ id: 'CLIENT_SEARCH_USER_TEXT' })}
                extraActionButtons={extraActionHeaderButtons}
                refreshData={{
                    current: refresh,
                    set: setRefresh,
                }}
                filters
                checkbox={isAdmin}
                disableCheckboxPerRow={row => row.original.id === userId}
                columns={[
                    ...(process.buildConfigs.enableUsernameColumn
                        ? [
                            {
                                accessor: 'userName',
                                sortName: 'UserName',
                                Header: translate({ id: 'CLIENT_USERNAME' }),
                                width: 25,
                                Cell: ({ row }: { row: Row<IUserWithLicenses> }) => (
                                    <div className={clsx(classes.userNameCell)}>
                                        <Tooltip
                                            arrow
                                            title={`${row.original.userName}
                                            ${getGroupsForUser(row.original.groupIDs)?.find(group => group === 'Administrators') ? translate({ id: 'CLIENT_ORG_ADMIN' }) : ''}`}>
                                            <span>
                                                {row.original.userName}
                                            </span>
                                        </Tooltip>
                                        {!isHostMode &&
                      getGroupsForUser(row.original.groupIDs)?.find(group => group === 'Administrators') ? (
                                                <Chip
                                                    className={clsx(classes.userRoleLabel)}
                                                    label={translate({ id: 'CLIENT_ORG_ADMIN' })}
                                                    style={{ color: themeId === 'dark' ? Tokens.Colors.ColorOrange700 : Tokens.Colors.ColorOrange300 }}
                                                />
                                            ) : null}
                                    </div>
                                ),
                            },
                            {
                                accessor: 'name',
                                sortName: 'Name',
                                Header: translate({ id: 'CLIENT_NAME' }),
                                width: 25,
                                Cell: ({ row }: { row: Row<IUserWithLicenses> }) => (
                                    <div>
                                        <Tooltip
                                            arrow
                                            title={`${row.original.name} ${row.original.surname ?? ''}`}>
                                            <span>
                                                {`${row.original.name} ${row.original.surname ?? ''}`}
                                            </span>
                                        </Tooltip>
                                    </div>
                                ),
                            },
                        ]
                        : [
                            {
                                accessor: 'userName',
                                sortName: 'Name',
                                Header: translate({ id: 'CLIENT_NAME' }),
                                width: 25,
                                Cell: ({ row }: { row: Row<IUserWithLicenses> }) => (
                                    <div className={clsx(classes.userNameCell)}>
                                        <Tooltip
                                            arrow
                                            title={`${getFullNameDuplicated(row.original.name + ' ' + row.original.surname)}
                                            ${getGroupsForUser(row.original.groupIDs)?.find(group => group === 'Administrators') ? translate({ id: 'CLIENT_ORG_ADMIN' }) : ''}`}>
                                            <span>
                                                {getFullNameDuplicated(`${row.original.name} ${row.original.surname ?? ''}`)}
                                            </span>
                                        </Tooltip>
                                        {getGroupsForUser(row.original.groupIDs)?.find(group => group === 'Administrators') ? (
                                            <Chip
                                                className={clsx(classes.userRoleLabel)}
                                                label={translate({ id: 'CLIENT_ORG_ADMIN' })}
                                                style={{ color: themeId === 'dark' ? Tokens.Colors.ColorOrange700 : Tokens.Colors.ColorOrange300 }}
                                            />
                                        ) : null}
                                    </div>
                                ),
                            },
                        ]),
                    {
                        accessor: 'email',
                        sortName: 'Email',
                        Header: translate({ id: 'CLIENT_EMAIL' }),
                        width: 25,
                    },
                    ...(!isHostMode
                        ? [
                            {
                                accessor: 'groups',
                                Header: translate({ id: 'CLIENT_GROUPS' }),
                                width: 25,
                                disableSortBy: true,
                                Cell: ({ row }: { row: Row<IUserWithLicenses> }) => {
                                    const groups = getGroupsForUser(row.original.groupIDs)?.join(', ');
                                    return groups ? (
                                        <Tooltip
                                            arrow
                                            placement="bottom-start"
                                            title={groups}>
                                            <span style={{
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                            }}>
                                                {groups}
                                            </span>
                                        </Tooltip>
                                    ) : (
                                        <span>
                                            {translate({ id: 'CLIENT_NO_GROUPS_FOUND' })}
                                        </span>
                                    );
                                },
                            },
                        ]
                        : []),
                    ...(EnableUserLicensing && isAdmin && !isUnlicensedMode
                        ? [
                            {
                                accessor: 'userBundleLicenses',
                                Header: translate({ id: 'CLIENT_LICENSES' }),
                                width: 25,
                                disableSortBy: true,
                                Cell: ({ row }: { row: Row<IUserWithLicenses> }) => {
                                    const {
                                        text, tooltip, Icon,
                                    } = getLicenseEntry(row.original);
                                    return (
                                        <Tooltip
                                            title={tooltip}
                                            placement="bottom-start">
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}>
                                                {Icon && <Icon className={clsx(classes.licenseIcon, classes.icon)} />}
                                                <span data-cy="user-license">
                                                    {text}
                                                </span>
                                            </div>
                                        </Tooltip>
                                    );
                                },
                            },
                        ]
                        : []),
                    {
                        accessor: 'lastLoginTime',
                        sortName: 'LastLoginTime',
                        Header: translate({ id: 'CLIENT_LAST_ACTIVE' }),
                        width: 25,
                        Cell: ({ row }) => {
                            const user: IUserCIS = row.original;
                            return !user.lastLoginTime || new Date(user.lastLoginTime).getTime() <= 0 ? (
                                !process.buildConfigs.disableUserInvite &&
                  user.type !== UserType.DirectoryUser &&
                  !DisableFeatureFedRamp && (
                                    <span>
                                        {isAdmin ? translate({ id: 'CLIENT_PENDING' }) : translate({ id: 'CLIENT_PENDING_INVITATION' })}
                                        {isAdmin && (
                                            <Tooltip title={translate({ id: 'CLIENT_RESEND' })}>
                                                <button
                                                    className={classes.resendButton}
                                                    data-cy="resend-invite-button"
                                                    onClick={() => openResendInviteDialog(row)}
                                                >
                                                    {translate({ id: 'CLIENT_RESEND' })}
                                                </button>
                                            </Tooltip>
                                        )}
                                    </span>
                                )
                            ) : (
                                <FormattedDate
                                    value={new Date(user.lastLoginTime)}
                                    year="numeric"
                                    month="short"
                                    day="numeric" />
                            );
                        },
                    },
                ]}
                tableActions={
                    isAdmin
                        ? [
                            {
                                type: ButtonType.Button,
                                label: translate({ id: 'CLIENT_DELETE' }),
                                icon: <DeleteForeverOutlinedIcon />,
                                actionType: GridActionType.Selected,
                                click: openDeleteDialog,
                            },
                        ]
                        : []
                }
                rowActions={getRowActions()}
            />
        </>
    );
};

export default UsersCISPageComponent;
